import { FaTimesCircle, FaUndo } from 'react-icons/fa'
import { useDisclosure } from '@mantine/hooks'
import {
  Text,
  Modal,
  Button,
  Image,
  useMantineTheme,
  Card,
  LoadingOverlay,
  Group,
  ActionIcon,
  Tooltip,
  Menu,
  Box,
  MantineTheme,
} from '@mantine/core'
import { FC, useState } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { DriverResponse, ScheduledDriveResponse, TrackedDriverTimeType } from '@/api/main/types.gen'
import VehicleStateBadge from './VehicleStateBadge'
import { TbNavigationShare, TbPhotoFilled } from 'react-icons/tb'
import { ImageType } from './types'
import useGetVehicleImage from '@/hooks/useVehiclesData'
import { Link } from '@tanstack/react-router'
import styles from './VehicleCard.module.css'
import { getDataSyncOptions, getNavigationOptions, getPhotosOptions } from '@/api/navigationApi'
import { getRouteOptions } from '@/api/main'
import { useQuery } from '@tanstack/react-query'
import { extendKey } from '@/utils/queryKey'
import {
  VEHICLE_DATA_SYNC_REFETCH_INTERVAL_MS,
  VEHICLE_DATA_SYNC_STALE_TIME_MS,
  VEHICLE_NAVIGATION_STALE_TIME_MS,
} from '@/constants'
import { useNavigationClient } from '@/api-proxy/useProxiedClient'
import { DataSyncResponse } from '@/api/navigationApi/types.gen'
import { useNavigationCollapse } from '@/hooks/useNavigationCollapse'
import useBlobUrl from '@/hooks/useBlobUrl'
import RippleRow from './RippleRow'

const IMAGE_TYPE_KEYS: Record<ImageType, string> = {
  thumbnail: 'thumbnail',
  photos: 'combinedCameras',
  'raw-photos': 'combinedCamerasRaw',
  'photos-3d': 'camera3d',
  'photo-hq/FL': 'frontLeftHq',
  'photo-hq/FC': 'frontCenterHq',
  'photo-hq/FR': 'frontRightHq',
  'raw-photo-hq/FL': 'frontLeftHqRaw',
  'raw-photo-hq/FC': 'frontCenterHqRaw',
  'raw-photo-hq/FR': 'frontRightHqRaw',
}

type Translate = (key: Parameters<TFunction>[0]) => string

export const describeImageType = (imageType: ImageType, t?: Translate): string => {
  const key = IMAGE_TYPE_KEYS[imageType]
  if (t && key) return t(key as Parameters<TFunction>[0])
  return imageType
}

const SwitchViewButton: FC<{ size?: number; onSwitchView: (imageType: ImageType) => void }> = ({
  size = 28,
  onSwitchView,
}) => {
  const [opened, setOpened] = useState(false)
  const theme = useMantineTheme()
  const { t } = useTranslation('vehicles')

  return (
    <Menu opened={opened} onChange={setOpened}>
      <Menu.Target>
        <Tooltip label={t('switchCameraView')}>
          <ActionIcon
            size={size}
            variant="default"
            maw={size}
            color="var(--mantine-color-dimmed)"
            onClick={() => setOpened(true)}
            styles={{
              root: {
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 3,
              },
            }}
          >
            <TbPhotoFilled size={20} color={theme.colors.gray[6]} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>{t('lowQuality')}</Menu.Label>
        <Menu.Item onClick={() => onSwitchView('thumbnail')}>{describeImageType('thumbnail', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('photos')}>{describeImageType('photos', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('raw-photos')}>{describeImageType('raw-photos', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('photos-3d')}>{describeImageType('photos-3d', t)}</Menu.Item>
        <Menu.Label>{t('highQuality')}</Menu.Label>
        <Menu.Item onClick={() => onSwitchView('photo-hq/FL')}>{describeImageType('photo-hq/FL', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('photo-hq/FC')}>{describeImageType('photo-hq/FC', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('photo-hq/FR')}>{describeImageType('photo-hq/FR', t)}</Menu.Item>
        <Menu.Label>{t('highQualityRaw')}</Menu.Label>
        <Menu.Item onClick={() => onSwitchView('raw-photo-hq/FL')}>{describeImageType('raw-photo-hq/FL', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('raw-photo-hq/FC')}>{describeImageType('raw-photo-hq/FC', t)}</Menu.Item>
        <Menu.Item onClick={() => onSwitchView('raw-photo-hq/FR')}>{describeImageType('raw-photo-hq/FR', t)}</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

const VehicleCardHeader: FC<{
  vehicleId: string
  state?: TrackedDriverTimeType
  goToGarage?: boolean
}> = ({ vehicleId, state, goToGarage = false }) => {
  const { setCollapsed } = useNavigationCollapse()
  const { t } = useTranslation('vehicles')

  const vehicleIdElement = (
    <Link
      to="/car-navigation/$source"
      params={{ source: vehicleId }}
      style={{
        color: 'light-dark(var(--mantine-color-blue-9), #4dabf7)',
      }}
      className={styles.cardHeadLink}
      onClick={() => setCollapsed(true)}
      // reloadDocument={true}
    >
      <TbNavigationShare size={16} />
      <Text fw={500} size={'md'}>
        {vehicleId.toUpperCase()}
      </Text>
    </Link>
  )

  return (
    <Group justify="space-between" align="center" gap="xs" mb="xs">
      <Group styles={{ root: { gap: '0.5rem' } }}>
        <Tooltip label={t('viewNavigation', { vehicleId: vehicleId.toUpperCase() })} withArrow position="top">
          {vehicleIdElement}
        </Tooltip>
        <RippleRow vehicleId={vehicleId} />
      </Group>
      <VehicleStateBadge state={state} goToGarage={goToGarage} />
    </Group>
  )
}

const VehicleCardSync: FC<{
  boxHeight: number
  theme: MantineTheme
  children: React.ReactNode
  syncData?: DataSyncResponse
}> = ({ syncData, boxHeight, theme, children }) => {
  const { t } = useTranslation('vehicles')
  if (syncData?.status === 'Finished' || syncData?.status === 'InProgress') {
    return (
      <Box h={boxHeight} bg={theme.colors.gray[3]}>
        <Text
          size="sm"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {syncData?.status === 'Finished' ? t('cableCopyingFinished') : t('cableCopyingInProgress')}
        </Text>
      </Box>
    )
  }

  return children
}

const ImageSection: FC<{
  url?: string
  onImageClick?: () => void
  boxHeight: number
  syncData?: DataSyncResponse
  isLoading: boolean
  error: boolean
}> = ({ url, onImageClick, boxHeight, syncData, isLoading, error }) => {
  const theme = useMantineTheme()

  if (syncData?.status !== 'Waiting' && !isLoading) {
    return (
      <VehicleCardSync syncData={syncData} boxHeight={boxHeight} theme={theme}>
        {!url ? (
          <>
            <Box h={boxHeight} bg={theme.colors.gray[3]} />
            <Text
              size="xl"
              style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <FaTimesCircle />
            </Text>
          </>
        ) : (
          <Image src={url} onClick={onImageClick} style={{ cursor: 'pointer' }} h={boxHeight} />
        )}
      </VehicleCardSync>
    )
  }

  if (isLoading) {
    return (
      <>
        <LoadingOverlay
          visible={isLoading}
          zIndex={2}
          overlayProps={{ blur: 1, bg: theme.colors.gray[3] }}
          loaderProps={{
            size: 'md',
            type: 'oval',
            speed: 1,
            radius: 'md',
            color: theme.colors.gray[7],
          }}
          h={boxHeight}
        />
        <Box h={boxHeight} bg={theme.colors.gray[3]} />
      </>
    )
  }

  if (error) {
    return (
      <>
        <Box h={boxHeight} bg={theme.colors.gray[3]} />
        <Text size="xl" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          <FaTimesCircle />
        </Text>
      </>
    )
  }

  return <Image src={url} onClick={onImageClick} style={{ cursor: 'pointer' }} h={boxHeight} />
}

const VehicleCardContent: FC<{
  driver?: DriverResponse
  currentDrive?: ScheduledDriveResponse
  onSwitchView: (imageType: ImageType) => void
  boxHeight: number
  isLoading: boolean
  error: boolean
  url?: string
  onImageClick?: () => void
  routeProgress?: string | null
  syncData?: DataSyncResponse
  routeName?: string | null
}> = ({
  driver,
  currentDrive,
  onSwitchView,
  boxHeight,
  isLoading,
  error,
  url,
  onImageClick,
  routeProgress,
  syncData,
  routeName,
}) => {
  const theme = useMantineTheme()
  const { t } = useTranslation('vehicles')

  return (
    <Card shadow="sm" padding="xs" radius="md" withBorder className={styles.hoverableCard}>
      <Card.Section style={{ position: 'relative' }}>
        <ImageSection
          url={url}
          onImageClick={onImageClick}
          boxHeight={boxHeight}
          syncData={syncData}
          isLoading={isLoading}
          error={error}
        />
        <SwitchViewButton onSwitchView={onSwitchView} />
      </Card.Section>
      <Group justify="space-between" mt="xs" wrap="nowrap">
        <Text
          size="sm"
          styles={{
            root: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: driver?.name ? 'inherit' : 'var(--mantine-color-gray-6)',
            },
          }}
        >
          {driver?.name || t('noDriver')}
        </Text>

        <Text size="sm" styles={{ root: { whiteSpace: 'nowrap' } }}>
          {routeProgress ? `${routeProgress}%` : null}
        </Text>
      </Group>
      {currentDrive ? (
        <Link
          to="/drive-details/$id"
          params={{ id: currentDrive?.id.toString() }}
          style={{
            textDecoration: 'underline',
            fontSize: theme.fontSizes.sm,
            color: 'var(--mantine-color-text)',
          }}
        >
          #{currentDrive?.id}
        </Link>
      ) : (
        <Text size="sm" styles={{ root: { whiteSpace: 'nowrap', color: 'var(--mantine-color-gray-6)' } }}>
          No drive
        </Text>
      )}
      {currentDrive ? (
        <Link
          to="/route/$routeId/$version"
          params={{
            routeId: currentDrive.route_id.toString(),
            version: currentDrive.route_version.toString(),
          }}
          style={{
            textDecoration: 'underline',
            fontSize: theme.fontSizes.sm,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--mantine-color-text)',
          }}
        >
          {routeName || currentDrive.route_id}
        </Link>
      ) : (
        <Text size="sm" styles={{ root: { whiteSpace: 'nowrap', color: 'var(--mantine-color-gray-6)' } }}>
          No route
        </Text>
      )}
    </Card>
  )
}

const getImageHeight = (cardsPerRow: number) => {
  switch (cardsPerRow) {
    case 6:
      return 125
    case 4:
      return 150
    case 2:
      return 300
    default:
      return 200
  }
}

const VehicleCard: FC<{
  vehicleId: string
  state?: TrackedDriverTimeType
  driver?: DriverResponse
  cardsPerRow: number
  currentDrive?: ScheduledDriveResponse
  since?: string
}> = ({ vehicleId, state, driver, cardsPerRow, currentDrive }) => {
  const [imageType, setImageType] = useState<ImageType>('thumbnail')
  const [modalOpen, modalHandlers] = useDisclosure(false)
  const handleSwitchView = (imageType: ImageType) => setImageType(imageType)

  const client = useNavigationClient(vehicleId)

  const { data: syncData } = useQuery({
    ...extendKey(getDataSyncOptions({ client }), vehicleId),
    staleTime: VEHICLE_DATA_SYNC_STALE_TIME_MS,
    refetchInterval: VEHICLE_DATA_SYNC_REFETCH_INTERVAL_MS,
  })

  const { data: navigationData } = useQuery({
    ...extendKey(getNavigationOptions({ client }), vehicleId),
    staleTime: VEHICLE_NAVIGATION_STALE_TIME_MS,
  })

  // Card image - uses thumbnail by default
  const {
    url: image,
    isLoading,
    error,
    isRefetching,
    refetch: refetchCardImage,
  } = useGetVehicleImage({
    imageType,
    vehicleId,
    client,
    syncStatus: syncData?.status,
  })

  const {
    data: modalImage,
    refetch: refetchModal,
    isRefetching: isRefetchingModal,
    isFetching: isFetchingModal,
  } = useQuery({
    ...extendKey(getPhotosOptions({ client }), vehicleId, imageType),
    enabled: modalOpen && imageType === 'thumbnail',
  })

  const modalUrl = useBlobUrl(modalImage as Blob | undefined)

  // Determine which URL to use in modal: use photos if thumbnail, otherwise use the card image
  const displayModalUrl = imageType === 'thumbnail' ? modalUrl : image
  const isModalLoading = imageType === 'thumbnail' ? isFetchingModal || !modalUrl : isRefetching || !image

  const routeProgress =
    navigationData?.route_progress !== undefined ? (100 * navigationData?.route_progress).toFixed() : null

  const { data: route } = useQuery({
    ...getRouteOptions({
      path: {
        route_id: currentDrive?.route_id.toString() || '',
        route_version: currentDrive?.route_version.toString() || '',
      },
    }),
    enabled: !!currentDrive?.route_id && !!currentDrive?.route_version,
  })

  return (
    <>
      <Modal.Root opened={modalOpen} onClose={modalHandlers.close} size="90vw">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{vehicleId.toUpperCase()}</Modal.Title>
            <Button
              leftSection={<FaUndo />}
              variant="outline"
              onClick={() => {
                if (imageType === 'thumbnail') {
                  refetchModal()
                } else {
                  refetchCardImage()
                }
              }}
              loading={imageType === 'thumbnail' ? isRefetchingModal : isRefetching}
              ml="xs"
            >
              Reload
            </Button>
            <Modal.CloseButton />
          </Modal.Header>
          {
            <Modal.Body>
              <Box style={{ position: 'relative', minHeight: '400px', width: '100%' }}>
                <LoadingOverlay
                  visible={isModalLoading}
                  zIndex={2}
                  overlayProps={{ blur: 1 }}
                  loaderProps={{
                    size: 'lg',
                    type: 'oval',
                  }}
                />
                {displayModalUrl && <Image src={displayModalUrl} style={{ width: '100%', height: 'auto' }} />}
              </Box>
            </Modal.Body>
          }
        </Modal.Content>
      </Modal.Root>
      <VehicleCardHeader vehicleId={vehicleId} state={state} goToGarage={navigationData?.go_to_garage} />
      <VehicleCardContent
        syncData={syncData}
        driver={driver}
        currentDrive={currentDrive}
        onSwitchView={handleSwitchView}
        boxHeight={getImageHeight(cardsPerRow)}
        isLoading={isLoading || isRefetching}
        error={!!error}
        url={image}
        onImageClick={modalHandlers.open}
        routeProgress={routeProgress}
        routeName={route?.name || null}
      />
    </>
  )
}

export default VehicleCard
